/**
 * ErrorBoundary - Catches React errors and displays fallback UI
 */

import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Error boundary caught an error:', error, errorInfo);
  }

  handleRefresh = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/dashboard';
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-ybh-bg-primary flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-ybh-error/20 flex items-center justify-center">
              <AlertTriangle size={40} className="text-ybh-error" />
            </div>
            <h1 className="font-display text-3xl text-ybh-text-primary mb-3">
              SOMETHING WENT WRONG
            </h1>
            <p className="text-ybh-text-secondary mb-6">
              We hit an unexpected error. Try refreshing the page or go back to the dashboard.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <div className="mb-6 p-4 bg-ybh-bg-secondary border border-white/[0.04] rounded-lg text-left">
                <p className="text-xs text-ybh-error font-mono break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <button onClick={this.handleGoHome} className="btn-secondary">
                Go to Dashboard
              </button>
              <button onClick={this.handleRefresh} className="btn-primary flex items-center gap-2">
                <RefreshCw size={18} />
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
